import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MessageSquarePlus, FolderOpen, BarChart3, Sparkles, LogOut, User, Crown, Building2, FolderKanban, Shield, Camera, Loader2, Settings, Bell, CheckCircle2, XCircle, X, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { useAdd } from '@/contexts/AddContext';
import { usePendingJobs } from '@/contexts/PendingJobsContext';
import { avatarApi } from '@/services/avatarApi';
import sdoxImage from '@/pages/home/newLogo.png';
import { Button, Badge } from '@/components/ui';
import { errorMessages } from '@/lib/errors';
import { formatRelativeDate } from '@/lib/i18n-utils';

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, userTier, isAdmin, logout, user, setAvatarUrl } = useAuthContext();
  const { recordingState, duration: recordingDuration, transcriptionMode, streaming } = useAdd();
  const { pendingJobs, notifications, dismissNotification, dismissAllNotifications } = usePendingJobs();
  const [isAvatarOpen, setIsAvatarOpen] = useState(false);
  const [isBellOpen, setIsBellOpen] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bellCount = pendingJobs.length + notifications.length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(event.target as Node)) {
        setIsAvatarOpen(false);
      }
      if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setIsBellOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch avatar when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user && !user.avatarUrl) {
      avatarApi.getAvatar()
        .then(response => {
          if (response.exists && response.avatarUrl) {
            setAvatarUrl(response.avatarUrl);
          }
        })
        .catch(() => {
          // Silently fail - user just won't have an avatar
        });
    }
  }, [isAuthenticated, user, setAvatarUrl]);

  // Handle avatar file selection
  const handleAvatarChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('nav.invalidImageType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('nav.imageTooLarge'));
      return;
    }

    setIsUploadingAvatar(true);
    try {
      // Step 1: Get presigned upload URL
      const { uploadUrl, s3Key } = await avatarApi.getUploadUrl(file.name, file.type);

      // Step 2: Upload to S3
      await avatarApi.uploadAvatar(uploadUrl, file, file.type);

      // Step 3: Confirm upload and save S3 key to Cognito
      const { avatarUrl } = await avatarApi.confirmUpload(s3Key);

      // Step 4: Update local state with avatar URL
      setAvatarUrl(avatarUrl);
      setIsAvatarOpen(false);
    } catch (error) {
      alert(errorMessages.uploadAvatar(error));
    } finally {
      setIsUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [setAvatarUrl]);

  // Primary navigation items - always visible with specific colors
  const primaryItems = [
    { path: '/add', label: t('nav.add', 'Add'), icon: MessageSquarePlus, color: 'red' },
    { path: '/projects', label: t('nav.projects'), icon: FolderOpen, color: 'green' },
    { path: '/insights', label: t('nav.insights'), icon: BarChart3, color: 'purple' },
  ];

  // Items for "More" dropdown
  const moreItems = [
    { path: '/product', label: t('nav.enterprisePlatform'), icon: Sparkles },
  ];

  // Items for profile dropdown (above Account Settings)
  const profileNavItems = [
    { path: '/project-settings', label: t('nav.projectSettings'), icon: FolderKanban },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Get color classes for primary buttons
  const getColorClasses = (color: string, isActive: boolean) => {
    if (isActive) {
      switch (color) {
        case 'red':
          return 'bg-red-100 text-red-700 ring-2 ring-red-300';
        case 'green':
          return 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300';
        case 'purple':
          return 'bg-purple-100 text-purple-700 ring-2 ring-purple-300';
        default:
          return 'bg-blue-100 text-blue-700';
      }
    }
    switch (color) {
      case 'red':
        return 'bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700';
      case 'green':
        return 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700';
      case 'purple':
        return 'bg-purple-50 text-purple-600 hover:bg-purple-100 hover:text-purple-700';
      default:
        return 'bg-slate-100 text-slate-600 hover:bg-slate-200';
    }
  };

  // Get user tier display
  const getUserTierDisplay = () => {
    if (!isAuthenticated) {
      return { label: t('nav.guest'), icon: User, bgColor: 'bg-slate-100', textColor: 'text-slate-600' };
    }
    if (userTier === 'enterprise') {
      return { label: t('nav.enterprise'), icon: Building2, bgColor: 'bg-purple-100', textColor: 'text-purple-700' };
    }
    if (userTier === 'pro') {
      return { label: t('nav.pro'), icon: Crown, bgColor: 'bg-amber-100', textColor: 'text-amber-700' };
    }
    if (userTier === 'starter') {
      return { label: t('nav.starter'), icon: Zap, bgColor: 'bg-blue-100', textColor: 'text-blue-600' };
    }
    return { label: t('nav.trial'), icon: User, bgColor: 'bg-emerald-100', textColor: 'text-emerald-600' };
  };

  const tierDisplay = getUserTierDisplay();
  const TierIcon = tierDisplay.icon;

  // Check if any profile nav item is active (for avatar highlight)
  const isProfileNavActive = profileNavItems.some(item => location.pathname === item.path);

  return (
    <nav className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 sm:py-1.5 bg-gradient-to-r from-blue-50 via-white to-white rounded-full shadow-sm border border-slate-100">
      {/* Home with Logo */}
      <Link
        to="/"
        className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1 rounded-full font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
          location.pathname === '/'
            ? 'bg-blue-100 text-blue-700'
            : 'hover:bg-slate-100'
        }`}
      >
        <img src={sdoxImage} alt="SystemDox" className="h-8 sm:h-9" />
        <span className="hidden xs:inline font-bold text-slate-900">SystemDox</span>
      </Link>

      <div className="h-4 sm:h-5 w-px bg-slate-300 mx-0.5 sm:mx-1 shrink-0" />

      {isAuthenticated ? (
        <>
          {/* Primary Navigation - Record, Docs, Insights (authenticated only) */}
          {primaryItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2 rounded-full font-semibold text-xs sm:text-sm whitespace-nowrap transition-all ${getColorClasses(item.color, isActive)}`}
              >
                <Icon className="w-4 h-4 sm:w-4 sm:h-4 shrink-0" />
                <span className="hidden xs:inline">{item.label}</span>
              </Link>
            );
          })}

          {/* Recording Indicator — visible when recording on any page (batch or streaming) */}
          {(() => {
            const isBatchActive = recordingState === 'recording' || recordingState === 'paused';
            const isStreamingActive = transcriptionMode === 'streaming' && (
              streaming.sessionStatus === 'active' || streaming.sessionStatus === 'paused' || streaming.sessionStatus === 'connecting'
            );
            const isActive = isBatchActive || isStreamingActive;
            if (!isActive) return null;

            const activeDuration = isStreamingActive ? streaming.duration : recordingDuration;
            const isPulsing = isStreamingActive
              ? streaming.sessionStatus === 'active'
              : recordingState === 'recording';

            const m = Math.floor(activeDuration / 60);
            const s = activeDuration % 60;

            return (
              <Link
                to="/add"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-xs font-mono font-semibold"
                title={t('nav.recordingInProgress')}
              >
                <span className={`w-2 h-2 rounded-full bg-red-500 shrink-0 ${isPulsing ? 'animate-pulse' : ''}`} />
                {`${m}:${s.toString().padStart(2, '0')}`}
              </Link>
            );
          })()}

          {/* Notification Bell — shown when there are pending jobs or recent notifications */}
          {bellCount > 0 && (
            <div className="relative" ref={bellRef}>
              <button
                onClick={() => setIsBellOpen(!isBellOpen)}
                className={`relative flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full transition-colors ${
                  isBellOpen
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
                aria-label={`Notifications (${bellCount})`}
              >
                <Bell className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                  {bellCount > 9 ? '9+' : bellCount}
                </span>
              </button>

              {isBellOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 max-h-96 overflow-y-auto">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
                    <span className="text-sm font-semibold text-slate-900">{t('nav.notifications')}</span>
                    {notifications.length > 0 && (
                      <button
                        onClick={() => dismissAllNotifications()}
                        className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        {t('nav.clearAll')}
                      </button>
                    )}
                  </div>

                  {/* Pending / In-progress jobs */}
                  {pendingJobs.map((job) => (
                    <div
                      key={`pending-${job.jobId}`}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                    >
                      <Loader2 className="w-4 h-4 text-amber-500 animate-spin mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {job.templateName || t('nav.processing')}
                        </p>
                        {job.projectName && (
                          <p className="text-xs text-slate-500 truncate">{job.projectName}</p>
                        )}
                        <p className="text-xs text-amber-600 mt-0.5">
                          {job.statusMessage || t('nav.processing')}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Divider between pending and completed/failed */}
                  {pendingJobs.length > 0 && notifications.length > 0 && (
                    <div className="h-px bg-slate-100 mx-4" />
                  )}

                  {/* Completed / Failed notifications */}
                  {notifications.map((notification) => (
                    <div
                      key={`notif-${notification.job.jobId}`}
                      className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group ${
                        notification.type === 'completed' ? 'cursor-pointer' : ''
                      }`}
                      onClick={() => {
                        if (notification.type === 'completed' && notification.job.projectId) {
                          navigate(`/projects?project=${notification.job.projectId}`);
                          setIsBellOpen(false);
                          dismissNotification(notification.job.jobId);
                        }
                      }}
                    >
                      {notification.type === 'completed' ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {notification.job.templateName || t('nav.document')}
                        </p>
                        {notification.job.projectName && (
                          <p className="text-xs text-slate-500 truncate">{notification.job.projectName}</p>
                        )}
                        <p className={`text-xs mt-0.5 ${notification.type === 'completed' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {notification.type === 'completed' ? t('nav.readyToView') : (notification.job.error || t('nav.processingFailed'))}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {formatRelativeDate(notification.job.updatedAt)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissNotification(notification.job.jobId);
                        }}
                        className="p-1 text-slate-400 hover:text-slate-600 transition-all shrink-0 opacity-0 group-hover:opacity-100 sm:opacity-100"
                        aria-label="Dismiss"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Public Navigation (unauthenticated) — Free Templates & Enterprise Platform */}
          {moreItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2 rounded-full font-semibold text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-slate-200 text-slate-900'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Icon className="w-4 h-4 sm:w-4 sm:h-4 shrink-0" />
                <span className="hidden xs:inline">{item.label}</span>
              </Link>
            );
          })}
        </>
      )}

      {/* Auth Section */}
      {isLoading ? (
        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-slate-100 rounded-full animate-pulse" />
      ) : isAuthenticated ? (
        /* Avatar Dropdown for logged-in users */
        <div className="relative" ref={avatarRef}>
          {/* Hidden file input for avatar upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleAvatarChange}
            className="hidden"
          />

          <button
            onClick={() => setIsAvatarOpen(!isAvatarOpen)}
            disabled={isUploadingAvatar}
            className="relative flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold text-xs sm:text-sm hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm overflow-hidden"
          >
            {isUploadingAvatar ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.fullName || 'Avatar'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // If image fails to load, hide it and show initials
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : null}
            {/* Initials fallback - shown when no avatar or avatar fails to load */}
            {!isUploadingAvatar && !user?.avatarUrl && (
              <span>{user?.fullName ? user.fullName.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U'}</span>
            )}
          </button>

          {isAvatarOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
              {/* User Info with Avatar */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold overflow-hidden">
                      {user?.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt={user.fullName || 'Avatar'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-lg">
                          {user?.fullName ? user.fullName.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute -bottom-1 -right-1 p-1.5 bg-white rounded-full shadow-md border border-slate-200 hover:bg-slate-50 transition-colors"
                      title={t('nav.changeAvatar')}
                    >
                      <Camera className="w-3 h-3 text-slate-600" />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 truncate">
                      {user?.fullName || t('nav.user')}
                    </p>
                    <p className="text-sm text-slate-500 truncate">
                      {user?.email || ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tier Badge */}
              <Link
                to="/plan"
                className="block px-4 py-2 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                onClick={() => setIsAvatarOpen(false)}
              >
                <Badge variant={userTier === 'enterprise' ? 'enterprise' : userTier === 'pro' ? 'pro' : userTier === 'starter' ? 'starter' : 'free'} size="md" interactive>
                  <TierIcon className="w-3.5 h-3.5" />
                  {tierDisplay.label} {t('nav.plan')}
                </Badge>
              </Link>

              {/* Update Avatar Button */}
              <div className="px-2 pt-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                  className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isUploadingAvatar ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                  {isUploadingAvatar ? t('nav.uploading') : t('nav.updateAvatar')}
                </button>
              </div>

              {/* Project Settings & Templates */}
              <div className="px-2">
                {profileNavItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsAvatarOpen(false)}
                      className={`flex items-center gap-3 w-full px-3 py-2.5 text-sm rounded-lg transition-colors ${
                        isActive
                          ? 'bg-slate-100 text-slate-900'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>

              {/* Account Settings Link */}
              <div className="px-2">
                <Link
                  to="/account"
                  onClick={() => setIsAvatarOpen(false)}
                  className={`flex items-center gap-3 w-full px-3 py-2.5 text-sm rounded-lg transition-colors ${
                    location.pathname === '/account'
                      ? 'bg-slate-100 text-slate-900'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  {t('nav.accountSettings')}
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setIsAvatarOpen(false)}
                    className={`flex items-center gap-3 w-full px-3 py-2.5 text-sm rounded-lg transition-colors ${
                      location.pathname === '/admin'
                        ? 'bg-slate-100 text-slate-900'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Shield className="w-4 h-4" />
                    {t('nav.adminDashboard')}
                  </Link>
                )}
              </div>

              {/* Divider before logout */}
              <div className="h-px bg-slate-200 my-2 mx-2" />

              {/* Logout Button */}
              <div className="px-2">
                <button
                  onClick={() => {
                    setIsAvatarOpen(false);
                    handleLogout();
                  }}
                  className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  {t('nav.logOut')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Log in + Sign up buttons for logged-out users */
        <div className="flex items-center gap-1 sm:gap-2">
          <Link
            to="/login"
            state={{ from: location.pathname }}
            className="px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-slate-700 border border-slate-300 rounded-full hover:bg-slate-50 transition-colors whitespace-nowrap"
          >
            {t('nav.logIn')}
          </Link>
          <Link
            to="/signup"
            className="px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            {t('nav.signUp')}
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
